import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import UserMenu from './components/UserMenu';
import HomePage from './pages/HomePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetail from './pages/ProjectDetail';
import NewProject from './pages/NewProject';
import SettingsPage from './pages/SettingsPage';
import ClientPage from './pages/ClientPage';
import LogViewer from './pages/LogViewer';
import Toolbox from './pages/Toolbox';
import Analytics from './pages/Analytics';
import UsersAdmin from './pages/UsersAdmin';
import AIConfigPage from './pages/admin/AIConfigPage';
import BuddyAdminPage from './pages/admin/BuddyAdminPage';
import ThemesAdminPage from './pages/admin/ThemesAdminPage';
import ReportingAdminPage from './pages/admin/ReportingAdminPage';
import AlphaFeaturesPage from './pages/admin/AlphaFeaturesPage';
import { canViewAnalytics, canViewActivityLog, canCreateProject } from './permissions';
import GuestbookModal from './components/GuestbookModal';
import BuddyWidget from './components/BuddyWidget';
import LoginPage from './pages/LoginPage';
import ForcePasswordReset from './components/ForcePasswordReset';
import TvDashboard from './pages/TvDashboard';


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, handleLogin, logout } = useAuth();
  const [footerClicks, setFooterClicks] = useState(0);
  const [showGuestbook, setShowGuestbook] = useState(false);

  // Admin-controlled Memorial Day theme state (fetched once on mount, refreshed on theme-change).
  // Stored on window so the event-driven applyTheme always reads the latest value.
  const applyTheme = () => {
    const userChoice = localStorage.getItem('bidboard-theme');
    const userChoiceAt = localStorage.getItem('bidboard-theme-chosen-at');
    const memorial = window.__memorialThemeState || { state: 'hidden', appliedAt: null };

    let theme = userChoice || 'light';

    // Admin hid Memorial Day — never leave a user stuck on it
    if (memorial.state === 'hidden' && theme === 'memorial') theme = 'light';

    // Admin applied Memorial Day — auto-push unless user has chosen a theme since the apply event
    if (memorial.state === 'applied' && memorial.appliedAt) {
      const appliedAt = new Date(memorial.appliedAt).getTime();
      const chosenAt = userChoiceAt ? new Date(userChoiceAt).getTime() : 0;
      if (chosenAt < appliedAt) theme = 'memorial';
    }

    document.documentElement.classList.remove('dark-mode', 'hippie-mode', 'memorial-mode', 'vibe-mode');
    if (theme === 'dark') document.documentElement.classList.add('dark-mode');
    if (theme === 'hippie') document.documentElement.classList.add('hippie-mode');
    if (theme === 'memorial') document.documentElement.classList.add('memorial-mode');
    if (theme === 'vibe') document.documentElement.classList.add('vibe-mode');
  };

  useEffect(() => {
    applyTheme();
    // Fetch admin Memorial Day state, then re-apply (may swap theme if admin has pushed it)
    fetch('/api/public-settings/memorial-theme')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          window.__memorialThemeState = data;
          applyTheme();
        }
      })
      .catch(() => {});
    window.addEventListener('theme-change', applyTheme);
    fetch('/api/health').then(r => r.json()).then(d => { if (d.version) window.__bidboardVersion = d.version; }).catch(() => {});
    return () => window.removeEventListener('theme-change', applyTheme);
  }, []);

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/projects');
        setTimeout(() => {
          document.getElementById('global-search')?.focus();
        }, 100);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter, sans-serif', color: '#777' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>BidBoard</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (user.mustResetPassword && !user.impersonatedBy) {
    return <ForcePasswordReset onComplete={(updatedUser) => handleLogin(updatedUser)} onLogout={logout} />;
  }

  if (location.pathname === '/tv') {
    return <TvDashboard />;
  }

  const canCreate = canCreateProject(user);

  return (
    <div className="app-layout">
      <aside className="app-sidebar">
        <div className="sidebar-logo">
          <img src="/cm-corp-logo.png" alt="CM Corp" style={{ width: '105px', marginBottom: '6px' }} />
          <h1>BidBoard</h1>
        </div>
        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => isActive && !location.search ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <span>Home</span>
          </NavLink>

          {user.teamMemberId && (
            <NavLink to="/my-bids" className={({ isActive }) => isActive ? 'active' : ''}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span>My Bids</span>
            </NavLink>
          )}

          <div className="sidebar-divider" />

          <NavLink to="/projects" end className={({ isActive }) => isActive && !location.search ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            <span>All Projects</span>
          </NavLink>
          <Link to="/projects?status=Pipeline" className={location.pathname === '/projects' && location.search === '?status=Pipeline' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            <span>Opportunities</span>
          </Link>
          <Link to="/projects?status=Budget" className={location.pathname === '/projects' && location.search === '?status=Budget' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            <span>Budget</span>
          </Link>
          <Link to="/projects?status=Bidding" className={location.pathname === '/projects' && location.search === '?status=Bidding' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span>Bidding</span>
          </Link>
          <Link to="/projects?status=Pending" className={location.pathname === '/projects' && location.search === '?status=Pending' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>
            <span>Pending</span>
          </Link>
          <Link to="/projects?status=On+Hold" className={location.pathname === '/projects' && location.search === '?status=On+Hold' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            <span>On Hold</span>
          </Link>

          <div className="sidebar-divider" />

          <Link to="/projects?status=Awarded" className={location.pathname === '/projects' && location.search === '?status=Awarded' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span>Awarded</span>
          </Link>
          <Link to="/projects?status=Not+Awarded" className={location.pathname === '/projects' && location.search === '?status=Not+Awarded' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            <span>Not Awarded</span>
          </Link>

          <div className="sidebar-divider" />
          <Link to="/projects?archived=1" className={location.pathname === '/projects' && location.search === '?archived=1' ? 'active' : ''}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
            <span>Archive</span>
          </Link>

          {canCreate && (
            <>
              <div className="sidebar-divider" />
              <NavLink to="/projects/new">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                <span>New Project</span>
              </NavLink>
            </>
          )}

          <div style={{ marginTop: 'auto' }}>
            <div className="sidebar-divider" />
            {canViewAnalytics(user) && (
              <NavLink to="/analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><polyline points="7 14 11 10 15 14 21 8"/></svg>
                <span>Analytics</span>
              </NavLink>
            )}
            <NavLink to="/market-terminal" className={({ isActive }) => isActive ? 'active' : ''}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              <span>CMC Market Terminal</span>
            </NavLink>
            {/* TV renders outside the sidebar layout (see /tv special-case above)
                and is meant for a dedicated wall display — open it in a new tab. */}
            <a href="/tv" target="_blank" rel="noopener noreferrer">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              <span>BidBoard TV</span>
            </a>
          </div>

        </nav>

        <div className="sidebar-flag" aria-hidden="true" />

        <div className="sidebar-bottom-links">
          {canViewActivityLog(user) && (
            <NavLink to="/logs" className={({ isActive }) => isActive ? 'active' : ''}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              <span>Activity Log</span>
            </NavLink>
          )}
          {/* User management now lives inside /settings as an admin card.
              Route at /admin/users kept for deep links; no sidebar entry. */}
          <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            <span>Settings</span>
          </NavLink>
        </div>

        <div className="sidebar-user-section">
          <UserMenu />
        </div>
      </aside>

      <main className="app-main">
        <div className="memorial-banner" aria-label="Memorial Day tribute">
          <span className="memorial-stars" aria-hidden="true">&#9733;&nbsp;&#9733;&nbsp;&#9733;</span>
          <span className="memorial-text">Remembering the men and women who gave their tomorrows so we could have our todays.</span>
          <span className="memorial-stars" aria-hidden="true">&#9733;&nbsp;&#9733;&nbsp;&#9733;</span>
        </div>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/my-bids" element={<ProjectsPage myBids />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/new" element={<NewProject />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/clients/:name" element={<ClientPage />} />
          <Route path="/logs" element={<LogViewer />} />
          <Route path="/market-terminal" element={<Toolbox />} />
          {/* Legacy alias so bookmarks to /toolbox still resolve */}
          <Route path="/toolbox" element={<Toolbox />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/admin/users" element={<UsersAdmin />} />
          <Route path="/admin/ai" element={<AIConfigPage />} />
          <Route path="/admin/buddies" element={<BuddyAdminPage />} />
          <Route path="/admin/themes" element={<ThemesAdminPage />} />
          <Route path="/admin/reporting" element={<ReportingAdminPage />} />
          <Route path="/admin/alpha-features" element={<AlphaFeaturesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
        <footer className="page-footer" style={{ padding: '8px 24px' }}>
          <span
            onClick={() => {
              const next = footerClicks + 1;
              if (next >= 5) { setShowGuestbook(true); setFooterClicks(0); }
              else { setFooterClicks(next); }
            }}
            style={{ cursor: 'default', userSelect: 'none' }}
          >
            Internal Use Only &middot; Made with <span style={{ color: '#92752E' }}>&hearts;</span> by CM Corp IT &middot; v{window.__bidboardVersion || '...'}
          </span>
        </footer>
      </main>
      <BuddyWidget />
      {showGuestbook && <GuestbookModal onClose={() => setShowGuestbook(false)} />}
    </div>
  );
}

export default App;
